import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar, Button, MenuItem, Select, FormControl,
} from '@material-ui/core';
import ReplyIcon from '@material-ui/icons/Reply';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import StarRating from './StarRating';
import RemovalRequestDialog, { REMOVAL_REASONS } from './RemovalRequestDialog';

const useStyles = makeStyles(() => ({
  summary: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr',
    gap: 24,
    padding: 20,
    background: '#FFFFFF',
    border: '1px solid #E4E7EB',
    borderRadius: 12,
    marginBottom: 20,
  },
  bigScore: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    borderRight: '1px solid #EEF0F3',
    paddingRight: 24,
  },
  bigScoreNumber: {
    fontSize: 44,
    fontWeight: 700,
    color: '#1A2332',
    lineHeight: 1,
  },
  bigScoreMeta: {
    fontSize: 13,
    color: '#6C7480',
    marginTop: 8,
  },
  chipsRow: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  chip: {
    padding: '4px 10px',
    borderRadius: 999,
    background: '#F5F9FC',
    fontSize: 12,
    color: '#1A2332',
    fontWeight: 500,
  },
  chipAlert: {
    background: '#FDEDED',
    color: '#B91C1C',
  },
  distRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 6,
  },
  distLabel: {
    width: 32,
    fontSize: 13,
    color: '#6C7480',
    textAlign: 'right',
  },
  distBar: {
    flex: 1,
    height: 10,
    background: '#F1F3F6',
    borderRadius: 5,
    overflow: 'hidden',
  },
  distFill: {
    height: '100%',
    background: '#F5A623',
  },
  distCount: {
    width: 36,
    fontSize: 12,
    color: '#6C7480',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  toolbarSpacer: {
    flex: 1,
  },
  feed: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  reviewCard: {
    border: '1px solid #E4E7EB',
    borderRadius: 12,
    padding: 18,
    background: '#FFFFFF',
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
  },
  authorBlock: {
    flex: 1,
  },
  authorName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1A2332',
  },
  reviewMeta: {
    fontSize: 12,
    color: '#8A94A6',
    marginTop: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  reviewBody: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 1.55,
    color: '#2D3748',
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  needsReplyTag: {
    background: '#FDEDED',
    color: '#B91C1C',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  removalButton: {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 600,
    color: '#FFFFFF',
    background: '#B91C1C',
    borderColor: '#B91C1C',
    padding: '8px 18px',
    minWidth: 'auto',
    boxShadow: '0 1px 2px rgba(185, 28, 28, 0.25)',
    '&:hover': {
      borderColor: '#991818',
      color: '#FFFFFF',
      background: '#991818',
      boxShadow: '0 2px 6px rgba(185, 28, 28, 0.35)',
    },
  },
  removalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  removalBadgeRequested: {
    background: '#FFF6E0',
    color: '#B26A00',
  },
  removalBadgeInProgress: {
    background: '#E3F2FD',
    color: '#1565C0',
  },
  removalBadgeDone: {
    background: '#E8F5E9',
    color: '#2E7D32',
  },
  removalNote: {
    marginTop: 14,
    padding: 12,
    borderRadius: 10,
    background: '#FFF6E0',
    border: '1px solid #F2D488',
    fontSize: 13,
    color: '#5C3D00',
    lineHeight: 1.5,
  },
  removalNoteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#B26A00',
    marginBottom: 4,
  },
  removalNoteMeta: {
    fontSize: 11,
    color: '#8A6B00',
    fontWeight: 400,
    marginLeft: 4,
  },
  reply: {
    marginTop: 14,
    marginLeft: 52,
    padding: 14,
    borderRadius: 10,
    background: '#F5F9FC',
    border: '1px solid #E2ECF3',
  },
  replyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#0E7BAF',
  },
  replyBody: {
    marginTop: 6,
    fontSize: 13,
    color: '#2D3748',
    lineHeight: 1.5,
  },
  emptyState: {
    padding: 30,
    textAlign: 'center',
    color: '#8A94A6',
    fontSize: 14,
  },
  projection: {
    marginTop: 14,
    padding: '10px 12px',
    borderRadius: 10,
    background: '#EAF7EE',
    border: '1px solid #B7E0C2',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  projectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#1F7A3A',
  },
  projectionRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  projectionValue: {
    fontSize: 22,
    fontWeight: 700,
    color: '#155724',
    lineHeight: 1.1,
  },
  projectionDelta: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1F7A3A',
  },
  projectionDeltaNegative: {
    color: '#B91C1C',
  },
  projectionMeta: {
    fontSize: 11,
    color: '#3F6B4A',
  },
}));

const formatDate = (dateStr) => {
  // If it's already a relative date string from Google (e.g., "11 months ago"), use it directly
  if (typeof dateStr === 'string' && /^\d+\s+(second|minute|hour|day|week|month|year)s?\s+ago$/i.test(dateStr)) {
    return dateStr;
  }
  // Handle "a week ago", "a month ago", etc.
  if (typeof dateStr === 'string' && /^a\s+(second|minute|hour|day|week|month|year)\s+ago$/i.test(dateStr)) {
    return dateStr;
  }

  const d = new Date(dateStr);
  // If invalid date, return the original string or fallback
  if (Number.isNaN(d.getTime())) {
    return dateStr || '';
  }

  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) === 1 ? '' : 's'} ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
};

const reasonLabel = (value) => {
  const r = REMOVAL_REASONS.find((x) => x.value === value);
  return r ? r.label : value;
};

const getRemovalBadgeClass = (status, classes) => {
  if (status === 'done') return classes.removalBadgeDone;
  if (status === 'in_progress') return classes.removalBadgeInProgress;
  return classes.removalBadgeRequested;
};

const getRemovalNoteStyle = (status) => {
  if (status === 'done') return { background: '#E8F5E9', borderColor: '#A5D6A7', color: '#1B5E20' };
  if (status === 'in_progress') return { background: '#E3F2FD', borderColor: '#90CAF9', color: '#0D47A1' };
  return undefined;
};

const getRemovalNoteHeaderStyle = (status) => {
  if (status === 'done') return { color: '#2E7D32' };
  if (status === 'in_progress') return { color: '#1565C0' };
  return undefined;
};

const ReviewsTab = ({
  location, removalRequests, onRequestRemoval,
  hasMore, loadingMore, onLoadMore,
  sortBy, onSortChange,
}) => {
  const classes = useStyles();
  const [dialogReview, setDialogReview] = useState(null);

  const { reviews, reviewSummary: summary } = location;

  const projection = useMemo(() => {
    const requested = reviews.filter((r) => removalRequests[r.id]);
    if (requested.length === 0) return null;
    const totalCount = summary.totalFromGoogle || summary.count;
    const currentAvg = summary.ratingFromGoogle || summary.avg;
    const remainingCount = totalCount - requested.length;
    if (remainingCount <= 0) return null;
    const totalSum = currentAvg * totalCount;
    const removedSum = requested.reduce((acc, r) => acc + r.rating, 0);
    const newAvg = (totalSum - removedSum) / remainingCount;
    return {
      pendingCount: requested.length,
      newAvg,
      delta: newAvg - currentAvg,
    };
  }, [reviews, removalRequests, summary]);

  return (
    <div>
      <div className={classes.summary} style={{ gridTemplateColumns: projection ? '1fr 1fr' : '1fr' }}>
        <div className={classes.bigScore}>
          <div className={classes.bigScoreNumber}>
            {(summary.ratingFromGoogle || summary.avg).toFixed(1)}
          </div>
          <StarRating value={summary.ratingFromGoogle || summary.avg} size={22} />
          <div className={classes.bigScoreMeta}>
            {summary.totalFromGoogle || summary.count} Google review{(summary.totalFromGoogle || summary.count) === 1 ? '' : 's'}
            {summary.count !== (summary.totalFromGoogle || summary.count) && (
              <span> · {summary.count} loaded</span>
            )}
          </div>
          <div className={classes.chipsRow}>
            {summary.unrepliedCount > 0 && (
              <span className={`${classes.chip} ${classes.chipAlert}`}>
                {summary.unrepliedCount} need reply
              </span>
            )}
          </div>
        </div>
        {projection && (
          <div className={classes.projection}>
            <span className={classes.projectionLabel}>
              Projected rating once approved
            </span>
            <div className={classes.projectionRow}>
              <span className={classes.projectionValue}>
                {projection.newAvg.toFixed(2)} ★
              </span>
              <span
                className={`${classes.projectionDelta} ${
                  projection.delta < 0 ? classes.projectionDeltaNegative : ''
                }`}
              >
                {projection.delta >= 0 ? '▲' : '▼'}{' '}
                {projection.delta >= 0 ? '+' : ''}
                {Math.abs(projection.delta) < 0.01
                  ? projection.delta.toFixed(4)
                  : projection.delta.toFixed(2)}
              </span>
            </div>
            <span className={classes.projectionMeta}>
              After {projection.pendingCount} pending removal
              {projection.pendingCount === 1 ? '' : 's'}{' '}
              {projection.pendingCount === 1 ? 'is' : 'are'} approved
            </span>
          </div>
        )}
      </div>

      <div className={classes.toolbar}>
        <div className={classes.toolbarSpacer} />
        <FormControl variant="outlined" size="small">
          <Select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            style={{ minWidth: 160, fontSize: 13 }}
          >
            <MenuItem value="newestFirst">Newest first</MenuItem>
            <MenuItem value="ratingHigh">Highest rated</MenuItem>
            <MenuItem value="ratingLow">Lowest rated</MenuItem>
            <MenuItem value="mostRelevant">Most relevant</MenuItem>
          </Select>
        </FormControl>
      </div>

      {reviews.length === 0 ? (
        <div className={classes.emptyState}>No reviews found.</div>
      ) : (
        <div className={classes.feed}>
          {reviews.map((r) => {
            const removal = removalRequests[r.id];
            return (
              <div key={r.id} className={classes.reviewCard}>
                <div className={classes.reviewHeader}>
                  <Avatar
                    className={classes.avatar}
                    style={{ backgroundColor: r.authorAvatarColor }}
                  >
                    {r.author.charAt(0)}
                  </Avatar>
                  <div className={classes.authorBlock}>
                    <div className={classes.authorName}>{r.author}</div>
                    <div className={classes.reviewMeta}>
                      <StarRating value={r.rating} size={14} />
                      <span>·</span>
                      <span>{formatDate(r.createdAt)}</span>
                    </div>
                  </div>
                  <div className={classes.headerRight}>
                    {!r.reply && !removal && (
                      <span className={classes.needsReplyTag}>Needs Reply</span>
                    )}
                    {removal && (
                      <span
                        className={`${classes.removalBadge} ${getRemovalBadgeClass(removal.status, classes)}`}
                      >
                        <CheckCircleOutlineIcon style={{ fontSize: 14 }} />
                        {removal.status === 'done' && 'Removal Complete'}
                        {removal.status === 'in_progress' && 'In Progress'}
                        {removal.status === 'requested' && 'Removal Requested'}
                        {!removal.status && 'Removal Requested'}
                      </span>
                    )}
                  </div>
                </div>
                <div className={classes.reviewBody}>{r.body}</div>
                {r.reply && (
                  <div className={classes.reply}>
                    <div className={classes.replyHeader}>
                      <ReplyIcon style={{ fontSize: 14 }} />
                      Response from the owner · {formatDate(r.reply.createdAt)}
                    </div>
                    <div className={classes.replyBody}>{r.reply.body}</div>
                  </div>
                )}
                {removal && (
                  <div
                    className={classes.removalNote}
                    style={getRemovalNoteStyle(removal.status)}
                  >
                    <div
                      className={classes.removalNoteHeader}
                      style={getRemovalNoteHeaderStyle(removal.status)}
                    >
                      <FlagOutlinedIcon style={{ fontSize: 14 }} />
                      {removal.status === 'done' && 'Removal complete'}
                      {removal.status === 'in_progress' && 'Removal in progress'}
                      {(removal.status === 'requested' || !removal.status) && 'Removal submitted to Hennessey team'}
                      <span className={classes.removalNoteMeta}>
                        · {formatDate(removal.requestedAt)}
                      </span>
                    </div>
                    <div>
                      <strong>Reason:</strong> {reasonLabel(removal.reason)}
                      {removal.notes ? (
                        <>
                          <br />
                          <strong>Notes:</strong> {removal.notes}
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loadingMore}
            style={{
              textTransform: 'none',
              borderColor: '#00B5E2',
              color: '#00B5E2',
              padding: '8px 32px',
            }}
          >
            {loadingMore ? 'Loading…' : 'Load More Reviews'}
          </Button>
        </div>
      )}

      <RemovalRequestDialog
        open={Boolean(dialogReview)}
        review={dialogReview}
        onClose={() => setDialogReview(null)}
        onSubmit={(payload) => {
          onRequestRemoval(dialogReview.id, {
            ...payload,
            nap_listing: location.id,
            review_google_id: dialogReview.id,
            review_author: dialogReview.author,
            review_rating: dialogReview.rating,
            review_body: dialogReview.body,
            review_created_at: dialogReview.createdAt,
          });
          setDialogReview(null);
        }}
      />
    </div>
  );
};

ReviewsTab.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  removalRequests: PropTypes.object.isRequired,
  onRequestRemoval: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  sortBy: PropTypes.string,
  onSortChange: PropTypes.func,
};

ReviewsTab.defaultProps = {
  hasMore: false,
  loadingMore: false,
  onLoadMore: () => {},
  sortBy: 'newestFirst',
  onSortChange: () => {},
};

export default ReviewsTab;
